import React from 'react';
import { ChecklistAnswerValue, ChecklistItemSnapshot } from '../../types';

export const ANSWER_LABELS: Record<ChecklistAnswerValue, string> = {
  [ChecklistAnswerValue.YES]: 'Yes',
  [ChecklistAnswerValue.NO]: 'No',
  [ChecklistAnswerValue.NOT_APPLICABLE]: 'N/A',
};

/** Draft state for one item, held by the parent page until Save flushes it. */
export interface ItemDraft {
  value?: ChecklistAnswerValue;
}

interface ChecklistItemRowProps {
  item: ChecklistItemSnapshot;
  /** The saved answer for this item, if any (from the run). */
  savedValue?: ChecklistAnswerValue;
  /** Legacy per-item comment on runs saved before the run note existed. */
  savedComment?: string;
  /** In-progress local edit, not yet saved. */
  draft: ItemDraft;
  onChange: (itemId: string, draft: ItemDraft) => void;
  disabled?: boolean;
}

/**
 * One checklist item: label and Yes / No / N-A buttons. A saved answer (from
 * the run) is shown alongside so a re-opened list still reads clearly after
 * a page reload, before any local edit happens.
 *
 * The explanation for a No lives in the checklist's single "Note for the
 * manager" box (owned by the page), not in a per-item field — one place to
 * write, one place to read. A legacy per-item comment is still displayed
 * when a run carries one.
 *
 * Never shows red/alarm styling for a No or an unanswered item — this is an
 * operational prompt, not a compliance gate (no Tusla/HSA claim, no alarm
 * styling for "incomplete").
 */
export const ChecklistItemRow: React.FC<ChecklistItemRowProps> = ({
  item,
  savedValue,
  savedComment,
  draft,
  onChange,
  disabled,
}) => {
  const value = draft.value ?? savedValue;

  return (
    <div
      data-testid={`checklist-item-row-${item.itemId}`}
      className="flex flex-col gap-2 border-b border-gray-100 py-3 last:border-b-0"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-900">{item.label}</span>
          {item.isRequired && <span className="text-xs text-gray-400">(required)</span>}
        </div>

        <div className="flex items-center gap-2">
          {savedValue && !draft.value && (
            <span
              data-testid={`checklist-answered-${item.itemId}`}
              className="text-xs text-gray-500"
            >
              {ANSWER_LABELS[savedValue]}
              {savedComment ? ` — ${savedComment}` : ''}
            </span>
          )}
          <div className="flex gap-1">
            {(Object.values(ChecklistAnswerValue) as ChecklistAnswerValue[]).map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`checklist-answer-${item.itemId}-${v}`}
                onClick={() => onChange(item.itemId, { value: v })}
                disabled={disabled}
                aria-pressed={value === v}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
                  value === v
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {ANSWER_LABELS[v]}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
